import { useMemo, useState } from "react";
import {
  Badge,
  Card,
  Flex,
  Heading,
  Select,
  Text,
  TextField,
  Tooltip,
  useThemeContext,
} from "@radix-ui/themes";
import {
  IconArrowRight,
  IconFileText,
  IconLink,
  IconMessage,
  IconPlayerPlay,
  IconTrash,
  IconUserCheck,
  IconUserX,
} from "@tabler/icons-react";
import { format } from "date-fns";
import Loader from "@/components/CustomUiComponents/Loader";
import useMobile from "@/lib/useMobile";
import { useEngagementAuditLog, useEngagements } from "@/services/engagement";

const ACTION_OPTIONS = [
  { value: "__all__", label: "All actions" },
  { value: "engagement_created", label: "Outreach started" },
  { value: "state_transition", label: "State change" },
  { value: "sms_sent", label: "SMS (single)" },
  { value: "sms_bulk_sent", label: "SMS (bulk)" },
  { value: "instagram_sent", label: "Instagram (single)" },
  { value: "instagram_bulk_sent", label: "Instagram (bulk)" },
  { value: "channel_changed", label: "Channel changed" },
  { value: "draft_uploaded", label: "Draft uploaded" },
  { value: "draft_approved", label: "Draft approved" },
  { value: "draft_rejected", label: "Draft rejected" },
  { value: "live_link_submitted", label: "Live link" },
  { value: "offer_accepted", label: "Offer accepted" },
  { value: "offer_declined", label: "Offer declined" },
  { value: "engagement_removed", label: "Outreach removed" },
];

const STATES = [
  "Pending",
  "Drafting",
  "Redrafting",
  "WaitingToPost",
  "Posted",
  "Declined",
  "Canceled",
] as const;

function actionIcon(action: string) {
  switch (action) {
    case "engagement_created":
      return <IconPlayerPlay size={18} />;
    case "state_transition":
      return <IconArrowRight size={18} />;
    case "sms_sent":
    case "sms_bulk_sent":
    case "instagram_sent":
    case "instagram_bulk_sent":
      return <IconMessage size={18} />;
    case "draft_uploaded":
    case "draft_approved":
    case "draft_rejected":
      return <IconFileText size={18} />;
    case "live_link_submitted":
      return <IconLink size={18} />;
    case "offer_accepted":
      return <IconUserCheck size={18} />;
    case "offer_declined":
      return <IconUserX size={18} />;
    case "engagement_removed":
      return <IconTrash size={18} />;
    default:
      return <IconArrowRight size={18} />;
  }
}

function performerLabel(
  type: string | undefined,
  email: string | null | undefined
) {
  if (type === "creator") return "Creator (via offer page)";
  if (type === "system" || (!email && !type)) return "System";
  return email || "Admin";
}

interface AuditLogRow {
  id: string;
  createdAt: string;
  action: string;
  fromState?: string | null;
  toState?: string | null;
  performedBy?: string | null;
  performedByEmail?: string | null;
  performedByType?: string;
  metadata?: Record<string, unknown> | null;
  description?: string | null;
  engagementId?: string | null;
  creator?: {
    id?: number;
    name?: string | null;
    username?: string;
    platform?: string;
  } | null;
}

export default function OutreachAuditTab({ campaignId }: { campaignId: number }) {
  const { appearance } = useThemeContext();
  const { isMobile } = useMobile();
  const [actionFilter, setActionFilter] = useState("__all__");
  const [creatorFilter, setCreatorFilter] = useState<string>("__all__");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const filters = useMemo(() => {
    const f: { creatorId?: number; action?: string; startDate?: string; endDate?: string } =
      {};
    if (actionFilter && actionFilter !== "__all__") f.action = actionFilter;
    if (creatorFilter && creatorFilter !== "__all__")
      f.creatorId = parseInt(creatorFilter, 10);
    if (startDate) f.startDate = new Date(startDate).toISOString();
    if (endDate) {
      const d = new Date(endDate);
      d.setHours(23, 59, 59, 999);
      f.endDate = d.toISOString();
    }
    return f;
  }, [actionFilter, creatorFilter, startDate, endDate]);

  const { data: engagements, isLoading: loadingEng } = useEngagements(campaignId);
  const { data: auditResponse, isLoading: loadingAudit } = useEngagementAuditLog(
    campaignId,
    filters,
    true
  );

  const logs: AuditLogRow[] = auditResponse?.logs ?? [];

  const stateCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const s of STATES) counts[s] = 0;
    if (!Array.isArray(engagements)) return counts;
    for (const e of engagements as { state?: string }[]) {
      if (e.state && counts[e.state] !== undefined) counts[e.state] += 1;
    }
    return counts;
  }, [engagements]);

  const creatorOptions = useMemo(() => {
    const map = new Map<number, string>();
    if (Array.isArray(engagements)) {
      for (const e of engagements as {
        creatorPrice?: { creator?: { id?: number; name?: string }; username?: string };
      }[]) {
        const c = e.creatorPrice?.creator;
        const id = c?.id;
        if (id != null) {
          const un = e.creatorPrice?.username || "?";
          map.set(id, `${c?.name || ""} @${un}`.trim());
        }
      }
    }
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [engagements]);

  const loading = loadingEng || loadingAudit;

  if (loading && !auditResponse && !engagements) {
    return (
      <Flex p="6" justify="center">
        <Loader full={false} />
      </Flex>
    );
  }

  return (
    <Flex direction="column" pt="4" gap="4" px={isMobile ? "5" : undefined}>
      <Flex direction="column" gap="2" pl="1">
        <Heading size="4">Outreach progress & audit</Heading>
        <Text size="2" color="gray">
          Timeline of outreach activity: who did what, which creator, and SMS/draft
          details. Actions from the public offer page are attributed to the creator.
        </Text>
      </Flex>

      <Card>
        <Flex direction="column" gap="3" p="3">
          <Text weight="medium" size="2">
            Current pipeline ({Array.isArray(engagements) ? engagements.length : 0}{" "}
            engagements)
          </Text>
          <Flex gap="2" wrap="wrap">
            {STATES.map((s) => (
              <Badge key={s} variant="soft" color="blue">
                {s}: {stateCounts[s] ?? 0}
              </Badge>
            ))}
          </Flex>
        </Flex>
      </Card>

      <Flex gap="3" wrap="wrap" align="center" pl="1">
        <Flex align="center" gap="2">
          <Text size="2" weight="medium">
            Creator
          </Text>
          <Select.Root
            value={creatorFilter}
            onValueChange={setCreatorFilter}
            size="2"
          >
            <Select.Trigger style={{ minWidth: 200 }} />
            <Select.Content>
              <Select.Item value="__all__">All creators</Select.Item>
              {creatorOptions.map(([id, label]) => (
                <Select.Item key={id} value={String(id)}>
                  {label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Flex>
        <Flex align="center" gap="2">
          <Text size="2" weight="medium">
            Action
          </Text>
          <Select.Root
            value={actionFilter}
            onValueChange={setActionFilter}
            size="2"
          >
            <Select.Trigger style={{ minWidth: 200 }} />
            <Select.Content>
              {ACTION_OPTIONS.map((o) => (
                <Select.Item key={o.value} value={o.value}>
                  {o.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Flex>
        <Flex
          align="center"
          gap="2"
          wrap="wrap"
          style={{
            colorScheme: appearance === "dark" ? "dark" : "light",
          }}
        >
          <Text size="2" weight="medium">
            From
          </Text>
          <TextField.Root
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            variant="soft"
            size="2"
            style={{ width: 160, maxWidth: "100%" }}
          />
          <Text size="2" weight="medium">
            To
          </Text>
          <TextField.Root
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            variant="soft"
            size="2"
            style={{ width: 160, maxWidth: "100%" }}
          />
        </Flex>
      </Flex>

      <Flex direction="column" gap="3" className="box" p="4">
        {loading && logs.length === 0 ? (
          <Loader full={false} />
        ) : logs.length === 0 ? (
          <Text color="gray" size="2">
            No audit entries yet. Activity will appear here as outreach runs, creators
            use the offer page, and drafts are reviewed.
          </Text>
        ) : (
          logs.map((log) => (
            <Card key={log.id} variant="surface">
              <Flex gap="3" p="3" align="start">
                <Flex
                  align="center"
                  justify="center"
                  style={{ minWidth: 36, marginTop: 2 }}
                >
                  {actionIcon(log.action)}
                </Flex>
                <Flex direction="column" gap="1" style={{ flex: 1 }}>
                  <Flex justify="between" align="start" wrap="wrap" gap="2">
                    <Text size="2" weight="bold">
                      {log.creator
                        ? `${log.creator.name || ""} @${log.creator.username} · ${log.creator.platform}`
                        : "Unknown creator"}
                    </Text>
                    <Tooltip content={format(new Date(log.createdAt), "PPpp")}>
                      <Text size="1" color="gray" style={{ cursor: "default" }}>
                        {format(new Date(log.createdAt), "MMM d, yyyy h:mm a")}
                      </Text>
                    </Tooltip>
                  </Flex>
                  <Text size="2" color="gray">
                    <Text weight="medium" as="span" color="gray">
                      {performerLabel(
                        log.performedByType,
                        log.performedByEmail ?? undefined
                      )}
                    </Text>
                    {" · "}
                    {log.description ||
                      log.action.replace(/_/g, " ")}
                  </Text>
                  {(log.fromState || log.toState) && (
                    <Text size="1" color="gray">
                      {log.fromState || "—"} → {log.toState || "—"}
                    </Text>
                  )}
                  {log.metadata && (
                    <AuditMetadata meta={log.metadata} action={log.action} />
                  )}
                </Flex>
              </Flex>
            </Card>
          ))
        )}
      </Flex>
    </Flex>
  );
}

function AuditMetadata({
  meta,
  action,
}: {
  meta: Record<string, unknown>;
  action: string;
}) {
  const body = meta.bodyPreview as string | undefined;
  const url = meta.url as string | undefined;
  const feedback = meta.feedback as string | undefined;
  const reason = meta.reason as string | undefined;

  return (
    <Flex direction="column" gap="1" mt="1">
      {(action === "sms_sent" ||
        action === "sms_bulk_sent" ||
        action === "instagram_sent" ||
        action === "instagram_bulk_sent") &&
        body && (
          <Text
            size="1"
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              padding: 8,
              borderRadius: 6,
              background: "var(--gray-a3)",
            }}
          >
            {body}
          </Text>
      )}
      {url && (
        <Text size="1" style={{ wordBreak: "break-all" }}>
          {url}
        </Text>
      )}
      {feedback && (
        <Text size="1" color="orange">
          Feedback: {feedback}
        </Text>
      )}
      {reason && (
        <Text size="1" color="gray">
          Reason: {reason}
        </Text>
      )}
    </Flex>
  );
}
